import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Menu, HelpCircle, HomeIcon, Users, ClipboardList, Eye, Shield, ArrowDownRight, ArrowUpRight, ArrowRight, Columns, Filter, Info } from 'lucide-react';
const CHART_DATA = [{
  month: 'May-25',
  low: 5,
  med: 10,
  high: 14
}, {
  month: 'Jun-25',
  low: 6,
  med: 12,
  high: 14
}, {
  month: 'Jul-25',
  low: 9,
  med: 14,
  high: 16
}, {
  month: 'Aug-25',
  low: 10,
  med: 22,
  high: 18
}, {
  month: 'Sep-25',
  low: 10,
  med: 31,
  high: 21
}, {
  month: 'Oct-25',
  low: 10,
  med: 36,
  high: 23
}, {
  month: 'Nov-25',
  low: 16,
  med: 41,
  high: 24
}, {
  month: 'Dec-25',
  low: 19,
  med: 47,
  high: 22
}, {
  month: 'Jan-26',
  low: 20,
  med: 50,
  high: 25
}, {
  month: 'Feb-26',
  low: 20,
  med: 52,
  high: 25
}, {
  month: 'Mar-26',
  low: 20,
  med: 55,
  high: 25
}, {
  month: 'Apr-26',
  low: 20,
  med: 54,
  high: 26
}];
const TABLE_DATA = [{
  company: 'STARK INDUSTRIES',
  score: 43,
  trend: -12,
  dbt: 34,
  balance: 42000,
  limit: 75000,
  group: 'H',
  assignedTo: 'Dave Davidson',
  added: '9/16/2025'
}, {
  company: 'DUNDER MIFFLIN PAPER CO.',
  score: 82,
  trend: 3,
  dbt: 8,
  balance: 12000,
  limit: 50000,
  group: 'L',
  assignedTo: 'Sarah Chen',
  added: '1/15/2026'
}, {
  company: 'WAYNE ENTERPRISES',
  score: 56,
  trend: -1,
  dbt: 29,
  balance: 38000,
  limit: 60000,
  group: 'M',
  assignedTo: 'Dave Davidson',
  added: '6/27/2025'
}, {
  company: 'WONKA INDUSTRIES',
  score: 26,
  trend: -2,
  dbt: 22,
  balance: 8000,
  limit: 15000,
  group: 'M',
  assignedTo: 'Mike Torres',
  added: '3/30/2026'
}, {
  company: 'ACME CORPORATION',
  score: 41,
  trend: -4,
  dbt: 45,
  balance: 28000,
  limit: 40000,
  group: 'M',
  assignedTo: 'Sarah Chen',
  added: '12/1/2025'
}, {
  company: 'OCEANIC AIRLINES',
  score: 0,
  trend: 0,
  dbt: 103,
  balance: 0,
  limit: 0,
  group: 'M',
  assignedTo: 'Mike Torres',
  added: '3/30/2026'
}];
export function MonitorDashboard({
  onComplete
}: {
  onComplete?: () => void;
}) {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const timeouts = [setTimeout(() => setPhase(1), 150), setTimeout(() => setPhase(2), 1500), setTimeout(() => setPhase(3), 3500), setTimeout(() => setPhase(4), 4500) // Click first row
    ];
    const completeTimer = setTimeout(() => {
      if (onComplete) onComplete();
    }, 7500);
    return () => {
      timeouts.forEach(clearTimeout);
      clearTimeout(completeTimer);
    };
  }, [onComplete]);
  return <div className="w-full h-full bg-white rounded-xl overflow-hidden flex flex-col text-sm border border-gray-200/50" data-id="element-847">
      {/* Product Header */}
      <div className="flex items-center gap-3 px-4 py-1.5 border-b border-gray-200 bg-white shrink-0 z-10 relative" data-id="element-848">
        <Menu className="w-4 h-4 text-gray-500" data-id="element-849" />
        <img src="https://cdn.magicpatterns.com/uploads/a5S8ZQgbN9jzaBrUDmr99x/Logo_CreditPulse_Black.png" alt="Credit Pulse" className="h-5 object-contain" data-id="element-850" />
        <div className="flex-1 mx-3 relative" data-id="element-851">
          <input type="text" placeholder="Search Companies" className="w-full pl-3 pr-8 py-1 text-xs border border-gray-200 rounded bg-gray-50 focus:outline-none" data-id="element-852" />
          <Search className="w-3 h-3 text-gray-400 absolute right-2.5 top-1/2 -translate-y-1/2" data-id="element-853" />
        </div>
        <HelpCircle className="w-4 h-4 text-gray-400" data-id="element-854" />
        <div className="w-5 h-5 rounded-full bg-gray-300 flex items-center justify-center text-[8px] font-bold text-white" data-id="element-855">
          SU
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden relative" data-id="element-856">
        {/* Left Sidebar */}
        <div className="w-10 bg-white border-r border-gray-200 flex flex-col items-center py-3 gap-3.5 shrink-0 z-10 relative" data-id="element-857">
          <div className="w-7 h-7 rounded-md bg-[#6C5CE7]/10 flex items-center justify-center" data-id="element-858">
            <HomeIcon className="w-4 h-4 text-[#6C5CE7]" data-id="element-859" />
          </div>
          <Users className="w-4 h-4 text-gray-400" data-id="element-860" />
          <ClipboardList className="w-4 h-4 text-gray-400" data-id="element-861" />
          <Eye className="w-4 h-4 text-gray-400" data-id="element-862" />
          <Shield className="w-4 h-4 text-gray-400" data-id="element-863" />
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden bg-gray-50/30 p-5 gap-4 relative" data-id="element-864">
          {/* Top Section */}
          <div className="flex gap-4 shrink-0 h-[180px]" data-id="element-865">
            {/* Stat Cards (2x2) */}
            <div className="w-64 grid grid-cols-2 gap-3" data-id="element-866">
              {/* Total Accounts */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: phase >= 1 ? 1 : 0,
              scale: phase >= 1 ? 1 : 0.9
            }} transition={{
              duration: 0.3,
              delay: 0
            }} className="bg-white rounded-xl border border-gray-200 p-3 flex flex-col justify-center shadow-sm" data-id="element-867">
                <span className="text-2xl font-bold text-gray-900 leading-none" data-id="element-868">
                  88
                </span>
                <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mt-1.5" data-id="element-869">
                  Total Accounts
                </span>
              </motion.div>
              {/* High Risk */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: phase >= 1 ? 1 : 0,
              scale: phase >= 1 ? 1 : 0.9
            }} transition={{
              duration: 0.3,
              delay: 0.1
            }} className="bg-white rounded-xl border border-red-100 border-l-4 border-l-red-400 p-3 flex flex-col justify-center shadow-sm" data-id="element-870">
                <span className="text-2xl font-bold text-red-600 leading-none" data-id="element-871">
                  19
                </span>
                <span className="text-[10px] font-medium text-red-500 uppercase tracking-wider mt-1.5" data-id="element-872">
                  High Risk
                </span>
              </motion.div>
              {/* Moderate Risk */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: phase >= 1 ? 1 : 0,
              scale: phase >= 1 ? 1 : 0.9
            }} transition={{
              duration: 0.3,
              delay: 0.2
            }} className="bg-white rounded-xl border border-amber-100 border-l-4 border-l-amber-400 p-3 flex flex-col justify-center shadow-sm" data-id="element-873">
                <span className="text-2xl font-bold text-amber-600 leading-none" data-id="element-874">
                  47
                </span>
                <span className="text-[10px] font-medium text-amber-500 uppercase tracking-wider mt-1.5" data-id="element-875">
                  Moderate Risk
                </span>
              </motion.div>
              {/* Low Risk */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: phase >= 1 ? 1 : 0,
              scale: phase >= 1 ? 1 : 0.9
            }} transition={{
              duration: 0.3,
              delay: 0.3
            }} className="bg-white rounded-xl border border-green-100 border-l-4 border-l-green-400 p-3 flex flex-col justify-center shadow-sm" data-id="element-876">
                <span className="text-2xl font-bold text-green-600 leading-none" data-id="element-877">
                  22
                </span>
                <span className="text-[10px] font-medium text-green-500 uppercase tracking-wider mt-1.5" data-id="element-878">
                  Low Risk
                </span>
              </motion.div>
            </div>

            {/* Chart */}
            <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm p-4 flex flex-col" data-id="element-879">
              <div className="flex justify-between items-center mb-4 shrink-0" data-id="element-880">
                <h3 className="text-sm font-bold text-gray-900" data-id="element-881">
                  Historical Pulse Score Summary
                </h3>
                <div className="flex items-center gap-3 text-[10px] font-medium text-gray-500" data-id="element-882">
                  <div className="flex items-center gap-1.5" data-id="element-883">
                    <div className="w-3 h-3 rounded-sm bg-red-400/80" data-id="element-884" /> Low
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-885">
                    <div className="w-3 h-3 rounded-sm bg-amber-400/80" data-id="element-886" />{' '}
                    Medium
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-887">
                    <div className="w-3 h-3 rounded-sm bg-green-400/80" data-id="element-888" /> High
                  </div>
                </div>
              </div>
              <div className="flex-1 relative flex items-end justify-between pl-8 pb-5" data-id="element-889">
                {/* Y-axis lines */}
                <div className="absolute inset-0 pl-8 flex flex-col justify-between pb-5" data-id="element-890">
                  {[100, 80, 60, 40, 20, 0].map(val => <div key={val} className="w-full border-t border-dashed border-gray-100 flex items-center relative" data-id="element-891">
                      <span className="absolute -left-7 text-[9px] text-gray-400 w-5 text-right" data-id="element-892">
                        {val}
                      </span>
                    </div>)}
                </div>
                {/* Bars */}
                {CHART_DATA.map((d, i) => {
                const lowH = d.low / 100 * 100;
                const medH = d.med / 100 * 100;
                const highH = d.high / 100 * 100;
                return <div key={d.month} className="relative flex flex-col justify-end items-center w-8 h-full z-10" data-id="element-893">
                      <div className="w-full flex flex-col justify-end h-full" data-id="element-894">
                        <motion.div className="w-full bg-green-400/80" initial={{
                      height: 0
                    }} animate={{
                      height: phase >= 2 ? `${highH}%` : 0
                    }} transition={{
                      duration: 0.5,
                      delay: 0.2 + i * 0.05
                    }} data-id="element-895" />
                        <motion.div className="w-full bg-amber-400/80" initial={{
                      height: 0
                    }} animate={{
                      height: phase >= 2 ? `${medH}%` : 0
                    }} transition={{
                      duration: 0.5,
                      delay: 0.2 + i * 0.05
                    }} data-id="element-896" />
                        <motion.div className="w-full bg-red-400/80" initial={{
                      height: 0
                    }} animate={{
                      height: phase >= 2 ? `${lowH}%` : 0
                    }} transition={{
                      duration: 0.5,
                      delay: 0.2 + i * 0.05
                    }} data-id="element-897" />
                      </div>
                      <span className="absolute -bottom-5 text-[9px] text-gray-500 whitespace-nowrap" data-id="element-898">
                        {d.month}
                      </span>
                    </div>;
              })}
              </div>
            </div>
          </div>

          {/* Bottom Section: Table */}
          <div className="flex-1 bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col overflow-hidden" data-id="element-899">
            <div className="px-4 py-3 border-b border-gray-100 flex justify-between items-center bg-white shrink-0" data-id="element-900">
              <h3 className="text-sm font-bold text-gray-900" data-id="element-901">
                Monitored Accounts
              </h3>
              <div className="flex items-center gap-4 text-[10px] font-bold text-[#6C5CE7] tracking-wider" data-id="element-902">
                <button className="flex items-center gap-1.5 hover:text-[#5a4cdb] transition-colors" data-id="element-903">
                  <Columns className="w-3.5 h-3.5" data-id="element-904" /> COLUMNS
                </button>
                <button className="flex items-center gap-1.5 hover:text-[#5a4cdb] transition-colors" data-id="element-905">
                  <Filter className="w-3.5 h-3.5" data-id="element-906" /> FILTERS
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto" data-id="element-907">
              <table className="w-full text-left border-collapse" data-id="element-908">
                <thead data-id="element-909">
                  <tr className="border-b border-gray-100 text-[11px] text-gray-500 uppercase tracking-wider bg-gray-50/50" data-id="element-910">
                    <th className="px-4 py-3 font-medium" data-id="element-911">Company</th>
                    <th className="px-4 py-3 font-medium" data-id="element-912">Pulse Score</th>
                    <th className="px-4 py-3 font-medium" data-id="element-913">1M Trend</th>
                    <th className="px-4 py-3 font-medium" data-id="element-914">DBT</th>
                    <th className="px-4 py-3 font-medium" data-id="element-915">Balance / Limit</th>
                    <th className="px-4 py-3 font-medium" data-id="element-916">Assigned To</th>
                    <th className="px-4 py-3 font-medium" data-id="element-917">Group</th>
                    <th className="px-4 py-3 font-medium text-right" data-id="element-918">Action</th>
                  </tr>
                </thead>
                <tbody data-id="element-919">
                  {TABLE_DATA.map((row, i) => {
                  const getScoreColor = (score: number) => {
                    if (score < 40) return 'text-red-600 bg-red-50 border-red-200';
                    if (score < 70) return 'text-amber-600 bg-amber-50 border-amber-200';
                    return 'text-green-600 bg-green-50 border-green-200';
                  };
                  const getTrendIcon = (trend: number) => {
                    if (trend < 0) return <ArrowDownRight className="w-3 h-3 text-red-500" data-id="element-920" />;
                    if (trend > 0) return <ArrowUpRight className="w-3 h-3 text-green-500" data-id="element-921" />;
                    return <ArrowRight className="w-3 h-3 text-gray-400" data-id="element-922" />;
                  };
                  const getTrendColor = (trend: number) => {
                    if (trend < 0) return 'text-red-500';
                    if (trend > 0) return 'text-green-500';
                    return 'text-gray-400';
                  };
                  const isClicked = phase >= 4 && i === 0;
                  return <motion.tr key={row.company} initial={{
                    opacity: 0,
                    y: 10
                  }} animate={{
                    opacity: phase >= 3 ? 1 : 0,
                    y: phase >= 3 ? 0 : 10,
                    backgroundColor: isClicked ? 'rgba(108, 92, 231, 0.05)' : 'transparent'
                  }} transition={{
                    duration: 0.3,
                    delay: i === 0 && phase >= 4 ? 0 : 0.1 + i * 0.1
                  }} className={`border-b border-gray-50 hover:bg-gray-50/50 transition-colors relative ${isClicked ? 'border-l-2 border-l-[#6C5CE7]' : 'border-l-2 border-l-transparent'}`} data-id="element-923">
                        <td className="px-4 py-3 text-[13px] font-medium text-gray-900" data-id="element-924">
                          {row.company}
                        </td>
                        <td className="px-4 py-3" data-id="element-925">
                          <span className={`inline-flex items-center justify-center w-9 h-7 rounded-full border text-[13px] font-bold ${getScoreColor(row.score)}`} data-id="element-926">
                            {row.score}
                          </span>
                        </td>
                        <td className="px-4 py-3" data-id="element-927">
                          <div className={`flex items-center gap-1 text-[13px] font-medium ${getTrendColor(row.trend)}`} data-id="element-928">
                            {getTrendIcon(row.trend)}
                            {Math.abs(row.trend)}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-[13px] text-gray-600" data-id="element-929">
                          {row.dbt}
                        </td>
                        <td className="px-4 py-3" data-id="element-930">
                          {row.limit > 0 ? <div className="flex flex-col gap-0.5 w-32" data-id="element-931">
                              <div className="flex justify-between text-[10px] text-gray-500" data-id="element-932">
                                <span data-id="element-933">${(row.balance / 1000).toFixed(0)}K</span>
                                <span data-id="element-934">
                                  {Math.round(row.balance / row.limit * 100)}%
                                </span>
                              </div>
                              <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-935">
                                <div className="h-full rounded-full bg-[#6C5CE7]" style={{
                            width: `${Math.round(row.balance / row.limit * 100)}%`
                          }} data-id="element-936" />
                              </div>
                              <div className="text-[10px] text-gray-400 text-right" data-id="element-937">
                                ${(row.limit / 1000).toFixed(0)}K limit
                              </div>
                            </div> : <div className="flex flex-col gap-0.5 w-32" data-id="element-938">
                              <div className="text-[10px] text-gray-400 flex items-center gap-1" data-id="element-939">
                                No data{' '}
                                <Info className="w-2.5 h-2.5 text-gray-300" data-id="element-940" />
                              </div>
                              <div className="w-full h-1.5 bg-gray-100 rounded-full" data-id="element-941" />
                            </div>}
                        </td>
                        <td className="px-4 py-3 text-[13px] text-gray-600" data-id="element-942">
                          {row.assignedTo}
                        </td>
                        <td className="px-4 py-3" data-id="element-943">
                          <div className="w-6 h-6 rounded-full bg-slate-700 text-white flex items-center justify-center text-[10px] font-bold" data-id="element-944">
                            {row.group}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right" data-id="element-945">
                          <button className={`text-xs font-medium transition-colors ${isClicked ? 'text-[#5a4cdb] scale-95' : 'text-[#6C5CE7] hover:text-[#5a4cdb]'}`} data-id="element-946">
                            View
                          </button>
                        </td>
                      </motion.tr>;
                })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>;
}